import {
  IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED,
  IS_EVIDENCE_ACCESS_OVERRIDE_ENABLED,
  IS_EVIDENCE_ACTIVATION_FLOW_FOR_TEAMS_ENABLED,
  IS_NHS_EVIDENCE_CONTACT_SALES_ENABLED,
} from '@/components/constants/flags';
import { useIsProjectsEnabled } from '@/features/flags';
import { useIsShadowHeidiEnabled } from '@/features/shadowHeidi/hooks/useIsShadowHeidiEnabled';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { isEeaCountry } from '@/lists/countriesToRegion';
import { AccessTier, Region } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useCombinedQueriesTeamGeneralInfo } from './query/useCombinedQueriesTeamGeneralInfo';
import { useQueryKindeProperties } from './query/useQueryKindeProperties';

const NHS_EMAIL_DOMAINS = ['nhs.net', 'nhs.uk'] as const;

export function isNhsEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return NHS_EMAIL_DOMAINS.some((nhs) => domain === nhs || domain.endsWith(`.${nhs}`));
}

/**
 * Hook to get evidence access tier from UserResponse and derive
 * whether sessionless and in-session evidence should be shown.
 *
 * For NHS users (flag-gated), the flow is:
 *   1. Contact sales page (has_contacted_sales_for_evidence = false)
 *   2. Disclaimer page (once sessionless evidence is enabled by ops)
 *   3. Evidence chat (once disclaimer is accepted)
 *
 * Heidi Work (`useIsProjectsEnabled`) implies Evidence UI is available —
 * Work users should not hit a separate Evidence entitlement wall for the
 * same surfaces Work already depends on (chat sidebar, library, Ask Heidi).
 * Shadow Heidi still forces Evidence off.
 *
 * Defaults to NOACCESS when evidence_access is not present.
 */
export function useEvidenceAccess() {
  const { data: evidenceAccess, isPending } = useQueryUser(
    (user) => user?.evidence_access
  );
  const { data: kindeProperties } = useQueryKindeProperties();
  const { data: userCountry } = useQueryUser((user) => user?.country);
  const { hasTeam } = useCombinedQueriesTeamGeneralInfo();
  // UKI-218: Shadow Heidi mode disables Evidence + Ask Heidi entirely so
  // clinicians cannot consult external knowledge during the trial.
  const isShadowHeidi = useIsShadowHeidiEnabled();
  // Work umbrella: enabling Projects also unlocks Evidence UI (without
  // rewriting the stored access tier used for billing/admin displays).
  const isWorkEnabled = useIsProjectsEnabled();

  const resolvedAccess = isShadowHeidi
    ? AccessTier.NOACCESS
    : (evidenceAccess ?? AccessTier.NOACCESS);

  // if the access tier is not NOACCESS, then evidence has not been activated for the team
  // if the access tier is REVOKED, then evidence has been revoked for the team member
  const hasTeamAccess = resolvedAccess !== AccessTier.NOACCESS;
  const hasAccess =
    resolvedAccess !== AccessTier.NOACCESS && resolvedAccess !== AccessTier.REVOKED;

  const isNhsEvidenceContactSalesEnabled = IS_NHS_EVIDENCE_CONTACT_SALES_ENABLED();
  const isEvidenceActivationFlowForTeamsEnabled =
    IS_EVIDENCE_ACTIVATION_FLOW_FOR_TEAMS_ENABLED();

  const isNhsUser = useMemo(() => {
    // only turn it on for those in the condition in posthog so we test easily
    if (isNhsEvidenceContactSalesEnabled) return true;
    return false;
  }, [isNhsEvidenceContactSalesEnabled]);

  const isSessionlessEvidenceShown = useMemo(() => {
    if (isPending) return false;
    if (isShadowHeidi) return false;
    if (IS_EVIDENCE_ACCESS_OVERRIDE_ENABLED() || isWorkEnabled) return true;
    return hasAccess;
  }, [hasAccess, isPending, isShadowHeidi, isWorkEnabled]);

  const isInSessionEvidenceShown = useMemo(() => {
    if (isPending || !kindeProperties?.region) return false;
    if (isShadowHeidi) return false;
    if (IS_EVIDENCE_ACCESS_OVERRIDE_ENABLED()) return true;
    if (kindeProperties.region === Region.UK) return false;
    // INC-408: Switzerland is not EEA, but in-session Evidence treats it like
    // the EU — whether hosted on the CH stack or on EU.
    if (kindeProperties.region === Region.CH) return false;
    if (userCountry === 'Japan' || userCountry === 'Switzerland') return false;
    if (
      kindeProperties.region === Region.EU ||
      kindeProperties.region === Region.SE_AZ1
    ) {
      if (!userCountry) return false;
      if (isEeaCountry(userCountry)) return false;
    }
    return (hasAccess || isWorkEnabled) && IS_ASK_HEIDI_V2_CHAT_COMPLETIONS_ENABLED();
  }, [
    hasAccess,
    isPending,
    isShadowHeidi,
    isWorkEnabled,
    kindeProperties?.region,
    userCountry,
  ]);

  const showManagedTeamActivationRequest =
    isEvidenceActivationFlowForTeamsEnabled &&
    !hasTeamAccess &&
    !isNhsUser &&
    hasTeam &&
    // Work users already have Evidence UI via the Projects umbrella — don't
    // block them on the team activation request interstitial.
    !isWorkEnabled;

  return {
    evidenceAccess: resolvedAccess,
    hasEvidenceAccess: hasAccess,
    hasTeamEvidenceAccess: hasTeamAccess,
    isSessionlessEvidenceShown,
    isInSessionEvidenceShown,
    isNhsUser,
    isPending: isPending,
    showManagedTeamActivationRequest,
  };
}
